import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ShieldCheck, AlertTriangle, RefreshCw, Scale, Bell, Radar, Settings2,
  Save, Check, ExternalLink, Plus, Trash2,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../../../services/api';

const API = `${API_BASE_URL}/api/admin/legal/compliance`;
const WATCH_API = `${API_BASE_URL}/api/admin/legal/watch`;

const STATUS = {
  conforme: { label: 'Conforme', cls: 'bg-emerald-500/15 text-emerald-600', Icon: ShieldCheck },
  a_revoir: { label: 'À revoir', cls: 'bg-amber-500/15 text-amber-600', Icon: AlertTriangle },
  non_conforme: { label: 'Non conforme', cls: 'bg-destructive/15 text-destructive', Icon: AlertTriangle },
  error: { label: 'Analyse indisponible', cls: 'bg-muted text-muted-foreground', Icon: AlertTriangle },
};

const SEVERITE = {
  haute: 'bg-destructive/15 text-destructive',
  moyenne: 'bg-amber-500/15 text-amber-600',
  info: 'bg-sky-500/15 text-sky-600',
};

const ConformiteJuridique = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Revue de conformité (existant)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // Veille légale (nouveau)
  const [watch, setWatch] = useState(null);
  const [watchLoading, setWatchLoading] = useState(true);
  const [watchRunning, setWatchRunning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [sources, setSources] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, auth);
      setData(res.data);
    } catch (e) {
      console.error('Erreur chargement conformité', e);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const loadWatch = useCallback(async () => {
    setWatchLoading(true);
    try {
      const res = await axios.get(WATCH_API, auth);
      setWatch(res.data);
      setKeywords((res.data?.config?.keywords || []).join('\n'));
      setSources(res.data?.config?.sources || []);
    } catch (e) {
      console.error('Erreur chargement veille', e);
    } finally {
      setWatchLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); loadWatch(); }, [load, loadWatch]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/run`, {}, auth);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors du lancement de la vérification');
    } finally {
      setRunning(false);
    }
  };

  const runWatch = async () => {
    setWatchRunning(true);
    try {
      await axios.post(`${WATCH_API}/run`, {}, auth);
      await loadWatch();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors du lancement de la veille');
    } finally {
      setWatchRunning(false);
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const kw = keywords.split('\n').map((s) => s.trim()).filter(Boolean);
      await axios.put(`${WATCH_API}/config`, { keywords: kw, sources }, auth);
      await loadWatch();
      setShowConfig(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingCfg(false);
    }
  };

  const ackAlert = async (id) => {
    try {
      await axios.post(`${WATCH_API}/alerts/${id}/ack`, { acknowledged: true, action_note: notes[id] || '' }, auth);
      await loadWatch();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const updateSource = (i, patch) => setSources((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const addSource = () => setSources((prev) => [...prev, { id: `src-${Date.now()}`, name: '', url: '', enabled: true }]);
  const removeSource = (i) => setSources((prev) => prev.filter((_, idx) => idx !== i));

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';
  const reports = data?.reports || [];
  const alerts = watch?.alerts || [];
  const unread = watch?.unread_count || 0;

  return (
    <div className="min-h-screen" data-testid="conformite-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-2">
          <Scale className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Conformité juridique &amp; veille légale</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Surveillance automatique du droit français applicable à PADAID (association loi 1901, siège social à <strong>Nice</strong>).
          L'IA détecte et vous <strong>signale</strong> tout changement pertinent — elle ne modifie jamais une page légale automatiquement.
        </p>

        {/* Bandeau de notification */}
        {unread > 0 && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="watch-notification-banner">
            <Bell className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-700">
              <strong>{unread}</strong> changement{unread > 1 ? 's' : ''} juridique{unread > 1 ? 's' : ''} détecté{unread > 1 ? 's' : ''} nécessite{unread > 1 ? 'nt' : ''} votre attention.
            </p>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Section VEILLE LÉGALE                                              */}
        {/* ------------------------------------------------------------------ */}
        <section className="mb-10" data-testid="legal-watch-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Radar className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Veille légale — changements détectés</h2>
              {unread > 0 && (
                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-xs font-bold" data-testid="watch-unread-badge">{unread}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowConfig((v) => !v)} className="flex items-center gap-2" data-testid="toggle-config-btn">
                <Settings2 className="h-4 w-4" /> Configurer
              </Button>
              <Button size="sm" onClick={runWatch} disabled={watchRunning} className="flex items-center gap-2" data-testid="run-watch-btn">
                <RefreshCw className={`h-4 w-4 ${watchRunning ? 'animate-spin' : ''}`} /> {watchRunning ? 'Analyse…' : 'Lancer la veille'}
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mb-4">
            Dernière vérification : <strong>{fmt(watch?.meta?.last_checked_at)}</strong>
            {watch?.meta?.next_check_at && <> · Prochaine (auto) : {fmt(watch.meta.next_check_at)}</>}
          </p>

          {/* Éditeur de configuration */}
          {showConfig && (
            <Card className="mb-4" data-testid="watch-config-card">
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-1">Mots-clés surveillés (un par ligne)</h3>
                  <textarea
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    rows={6}
                    className="w-full rounded-md border border-border bg-background p-2 text-sm font-mono"
                    data-testid="watch-keywords-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold">Sources officielles surveillées</h3>
                    <Button variant="outline" size="sm" onClick={addSource} className="flex items-center gap-1" data-testid="add-source-btn">
                      <Plus className="h-3.5 w-3.5" /> Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {sources.map((s, i) => (
                      <div key={s.id || i} className="flex items-center gap-2" data-testid={`source-row-${i}`}>
                        <input type="checkbox" checked={s.enabled !== false} onChange={(e) => updateSource(i, { enabled: e.target.checked })} data-testid={`source-enabled-${i}`} />
                        <input value={s.name || ''} onChange={(e) => updateSource(i, { name: e.target.value })} placeholder="Nom" className="flex-1 rounded-md border border-border bg-background p-1.5 text-xs" data-testid={`source-name-${i}`} />
                        <input value={s.url || ''} onChange={(e) => updateSource(i, { url: e.target.value })} placeholder="URL du flux RSS/Atom" className="flex-[2] rounded-md border border-border bg-background p-1.5 text-xs font-mono" data-testid={`source-url-${i}`} />
                        <button onClick={() => removeSource(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-source-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <Button size="sm" onClick={saveConfig} disabled={savingCfg} className="flex items-center gap-2" data-testid="save-config-btn">
                  <Save className="h-4 w-4" /> {savingCfg ? 'Enregistrement…' : 'Enregistrer la configuration'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Liste des alertes */}
          {watchLoading ? (
            <p className="text-muted-foreground">Chargement…</p>
          ) : alerts.length === 0 ? (
            <Card><CardContent className="p-6 text-muted-foreground">Aucun changement détecté pour le moment. Lancez la veille pour analyser les dernières publications officielles.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {alerts.map((a) => {
                const sevCls = SEVERITE[a.severite] || SEVERITE.moyenne;
                return (
                  <Card key={a.id} className={a.acknowledged ? 'opacity-70' : ''} data-testid={`watch-alert-${a.id}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${sevCls}`}>
                            <AlertTriangle className="h-3.5 w-3.5" /> {a.severite === 'haute' ? 'Priorité haute' : a.severite === 'info' ? 'Information' : 'À examiner'}
                          </span>
                          {a.acknowledged && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-600">
                              <Check className="h-3.5 w-3.5" /> Traité
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">{a.source} · {a.pub_date || fmt(a.created_at)}</span>
                      </div>

                      <h3 className="font-semibold text-sm">
                        {a.link ? (
                          <a href={a.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary" data-testid={`alert-link-${a.id}`}>
                            {a.title} <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : a.title}
                      </h3>

                      {a.impact && <p className="text-sm text-muted-foreground"><strong className="text-foreground">Impact&nbsp;:</strong> {a.impact}</p>}
                      {a.action_suggeree && <p className="text-sm text-muted-foreground"><strong className="text-foreground">Action suggérée&nbsp;:</strong> {a.action_suggeree}</p>}

                      {a.acknowledged ? (
                        a.action_note && <p className="text-xs text-muted-foreground border-l-2 border-emerald-500/40 pl-2">Note : {a.action_note}</p>
                      ) : (
                        <div className="flex flex-col sm:flex-row gap-2 pt-1">
                          <input
                            placeholder="Note : action réalisée (facultatif)"
                            value={notes[a.id] || ''}
                            onChange={(e) => setNotes((prev) => ({ ...prev, [a.id]: e.target.value }))}
                            className="flex-1 rounded-md border border-border bg-background p-2 text-xs"
                            data-testid={`alert-note-${a.id}`}
                          />
                          <Button size="sm" variant="outline" onClick={() => ackAlert(a.id)} className="flex items-center gap-2" data-testid={`ack-alert-${a.id}`}>
                            <Check className="h-4 w-4" /> Marquer comme traité
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Section REVUE DE CONFORMITÉ (existant)                             */}
        {/* ------------------------------------------------------------------ */}
        <section data-testid="compliance-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Revue de conformité du site (IA)</h2>
            </div>
            <Button size="sm" onClick={runNow} disabled={running} className="flex items-center gap-2" data-testid="run-compliance-btn">
              <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} /> {running ? 'Analyse…' : 'Vérifier maintenant'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Relecture mensuelle des pages légales du site au regard du droit en vigueur.
            {data?.meta?.next_review_at && <> Prochaine vérification (auto) : <strong>{fmt(data.meta.next_review_at)}</strong>.</>}
          </p>

          {loading ? (
            <p className="text-muted-foreground">Chargement…</p>
          ) : reports.length === 0 ? (
            <Card><CardContent className="p-6 text-muted-foreground">Aucune vérification enregistrée pour le moment.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {reports.map((r, i) => {
                const s = STATUS[r.status] || STATUS.a_revoir;
                return (
                  <Card key={i} data-testid={`compliance-report-${i}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>
                          <s.Icon className="h-3.5 w-3.5" /> {s.label}
                        </span>
                        <span className="text-xs text-muted-foreground">{fmt(r.created_at)} · {r.triggered_by === 'manuel' ? 'manuel' : 'automatique'}</span>
                      </div>
                      {r.summary && <p className="text-sm">{r.summary}</p>}
                      {r.suggestions?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold mb-1">Propositions de mise à jour</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                            {r.suggestions.map((sug, j) => <li key={j}>{sug}</li>)}
                          </ul>
                        </div>
                      )}
                      {r.findings?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold mb-1">Constats</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                            {r.findings.map((f, j) => <li key={j}>{f}</li>)}
                          </ul>
                        </div>
                      )}
                      {r.legal_references?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold mb-1">Références juridiques vérifiées</h3>
                          <ul className="list-disc pl-5 space-y-1 text-xs text-muted-foreground">
                            {r.legal_references.map((ref, j) => <li key={j}>{ref}</li>)}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ConformiteJuridique;
